/** Public email providers — not valid facility domains. */
const PUBLIC_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "mail.com",
  "msn.com",
  "me.com",
  "ymail.com",
]);

/**
 * Normalize a hostname to a registrable domain (best-effort: last two labels).
 * @param {string} hostname
 * @returns {string | null}
 */
function normalizeHostnameToDomain(hostname) {
  const host = String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
  if (!host || host.includes(" ")) return null;

  const parts = host.split(".").filter(Boolean);
  if (parts.length < 2) return null;

  const domain = parts.slice(-2).join(".");
  if (PUBLIC_EMAIL_DOMAINS.has(domain)) return null;
  return domain;
}

/**
 * Extract a facility email domain from a website URL.
 * Strips www. and rejects public-mail domains.
 *
 * @param {string} urlStr
 * @returns {string | null}
 */
export function extractDomainFromWebsite(urlStr) {
  if (!urlStr || typeof urlStr !== "string") return null;
  const trimmed = urlStr.trim();
  if (!trimmed) return null;

  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withProtocol);
    let hostname = parsed.hostname.toLowerCase();
    if (hostname.startsWith("www.")) {
      hostname = hostname.slice(4);
    }
    return normalizeHostnameToDomain(hostname);
  } catch (err) {
    console.warn("[extractDomainFromWebsite] Invalid URL:", urlStr, err?.message);
    return null;
  }
}

/**
 * Build facilityDomain object for a location doc.
 *
 * @param {{ websiteUrl?: string, proposedDomain?: string, source?: string, confirmedByAdmin?: boolean }} params
 * @returns {object | null}
 */
export function buildFacilityDomainField(params = {}) {
  const websiteUrl = typeof params.websiteUrl === "string" ? params.websiteUrl.trim() : "";
  const explicitDomain =
    typeof params.proposedDomain === "string" ? params.proposedDomain.trim().toLowerCase() : "";

  let proposed = explicitDomain || null;
  let source = params.source || null;

  if (!proposed && websiteUrl) {
    proposed = extractDomainFromWebsite(websiteUrl);
    if (proposed) source = "derived";
  }

  if (!proposed) return null;

  return {
    proposed,
    source: source || "admin",
    websiteUrl: websiteUrl || null,
    confirmedByAdmin: Boolean(params.confirmedByAdmin),
  };
}

/**
 * @param {string} email
 * @param {string} proposedDomain
 * @returns {boolean}
 */
export function emailMatchesProposedDomain(email, proposedDomain) {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const domain = String(proposedDomain || "").trim().toLowerCase();
  if (!normalizedEmail.includes("@") || !domain) return false;
  const emailDomain = normalizedEmail.split("@")[1];
  return emailDomain === domain;
}
